/**
 * Single source of truth for the S3 / MinIO public image base URL.
 *
 * WHY THIS EXISTS (build-config bug, 2026-07):
 *   Components previously read the base URL inline with a guard like
 *   `(typeof process !== 'undefined' && process.env && process.env.S3_IMAGE_BASE_URL) || fallback`.
 *   webpack's DefinePlugin replaces ONLY the `process.env.S3_IMAGE_BASE_URL`
 *   token with a string literal; it does NOT define bare `process` or
 *   `process.env`. So the surviving `typeof process !== 'undefined'` guard is
 *   evaluated at RUNTIME in the browser, where `process` is not a global ->
 *   the guard is false -> the ternary took the dead fallback branch and the
 *   inlined MinIO literal sat on an unreachable branch. Images rendered
 *   against a non-existent fallback domain.
 *
 *   The fix: reference `process.env.S3_IMAGE_BASE_URL` DIRECTLY (no
 *   `typeof process` guard) so DefinePlugin folds the whole expression to the
 *   build-time literal. This mirrors the one read site that always worked
 *   (mixins/helpers.js `getImage`).
 *
 * FALLBACK: when S3_IMAGE_BASE_URL is not defined at build time the value is
 * the empty string. We fall back to a SAME-ORIGIN relative prefix ('/images/')
 * -- not a fake external domain -- and warn loudly so a misconfigured build
 * surfaces as an obvious problem (missing/relative image) rather than a silent
 * request to a domain that does not exist.
 */

// Relative, same-origin sentinel. Intentionally NOT a real external host so a
// missing build arg fails visibly instead of masquerading as a working CDN.
export const FALLBACK_IMAGE_BASE_URL = '/images/';

let warned = false;

/**
 * Return the configured image base URL, or the same-origin fallback.
 *
 * `process.env.S3_IMAGE_BASE_URL` is inlined at build time by webpack
 * DefinePlugin (see config/prod.env.js). Do NOT wrap it in a `typeof process`
 * guard -- that guard survives into the runtime bundle and defeats the
 * inlining (see file header).
 */
export function getImageBaseUrl() {
    const configured = process.env.S3_IMAGE_BASE_URL;
    if (configured) {
        return configured;
    }
    if (!warned) {
        warned = true;
        // eslint-disable-next-line no-console
        console.warn(
            '[imageBaseUrl] S3_IMAGE_BASE_URL is not set in this build; '
            + 'falling back to the same-origin prefix "' + FALLBACK_IMAGE_BASE_URL
            + '". Images will not resolve until the build inlines S3_IMAGE_BASE_URL.'
        );
    }
    return FALLBACK_IMAGE_BASE_URL;
}

/**
 * Build an absolute image URL for an uploaded key/uuid.
 * `getImageBaseUrl()` already ends in a trailing slash.
 */
export function buildImageUrl(key) {
    if (!key) return null;
    return `${getImageBaseUrl()}${key}`;
}
